/**
 * Viewport Manager - Handles zoom, scroll, and page navigation
 *
 * Manages:
 * - Zoom levels and transformations
 * - Page navigation
 * - Scroll tracking with Intersection Observer
 * - Fit-to-width calculations
 */

import { useState, useCallback, useEffect } from 'react';
import { PDFPageInfo } from '../types';

export interface UseViewportManagerOptions {
  pagesInfo: PDFPageInfo[];
  containerRef: React.RefObject<HTMLDivElement>;
  minZoom?: number;
  maxZoom?: number;
  zoom: number; // Zoom state passed from parent
  setZoom: (zoom: number) => void; // Zoom setter passed from parent
}

export interface UseViewportManagerResult {
  currentPage: number;
  setCurrentPage: (page: number) => void;
  zoomIn: () => void;
  zoomOut: () => void;
  resetZoom: () => void;
  fitWidth: () => void;
  fitHeight: () => void;
  goToPage: (page: number) => void;
  nextPage: () => void;
  previousPage: () => void;
}

/**
 * Hook for managing viewport state (scroll, navigation)
 * Zoom state is managed by parent component for coordination with PDFRenderer
 */
export const useViewportManager = ({
  pagesInfo,
  containerRef,
  minZoom = 0.5,
  maxZoom = 3.0,
  zoom,
  setZoom,
}: UseViewportManagerOptions): UseViewportManagerResult => {
  const [currentPage, setCurrentPage] = useState(1);

  /**
   * Zoom in by 25%
   */
  const zoomIn = useCallback(() => {
    const newZoom = Math.min(zoom + 0.25, maxZoom);
    setZoom(newZoom);
  }, [zoom, maxZoom, setZoom]);

  /**
   * Zoom out by 25%
   */
  const zoomOut = useCallback(() => {
    const newZoom = Math.max(zoom - 0.25, minZoom);
    setZoom(newZoom);
  }, [zoom, minZoom, setZoom]);

  /**
   * Reset to 100% zoom
   */
  const resetZoom = useCallback(() => {
    setZoom(1);
  }, []);

  /**
   * Fit page width to container width
   */
  const fitWidth = useCallback(() => {
    if (!containerRef.current || pagesInfo.length === 0) return;

    // Get container width, fallback to parent if container has no width
    let containerWidth = containerRef.current.clientWidth;
    if (containerWidth === 0 && containerRef.current.parentElement) {
      containerWidth = containerRef.current.parentElement.clientWidth;
    }

    if (containerWidth === 0) return; // Still no width, can't calculate

    const padding = 40; // Account for pagesContainer padding (20px each side)

    // Get page's base width (at zoom 1.0)
    // pagesInfo[0].width is already scaled, divide by scale to get base dimensions
    const pageWidthAtCurrentScale = pagesInfo[0].width;
    const baseWidth = pageWidthAtCurrentScale / pagesInfo[0].scale;

    // Calculate zoom to fit width
    const calculatedZoom = (containerWidth - padding) / baseWidth;
    const newZoom = Math.max(minZoom, Math.min(calculatedZoom, maxZoom));

    setZoom(newZoom);
  }, [containerRef, pagesInfo, minZoom, maxZoom, setZoom]);

  /**
   * Fit page height to container height (one page per screen)
   */
  const fitHeight = useCallback(() => {
    if (!containerRef.current || pagesInfo.length === 0) return;

    // Get container height, fallback to parent if container has no height
    let containerHeight = containerRef.current.clientHeight;
    if (containerHeight === 0 && containerRef.current.parentElement) {
      containerHeight = containerRef.current.parentElement.clientHeight;
    }

    if (containerHeight === 0) return; // Still no height, can't calculate

    const padding = 20; // Minimal padding for fit height

    // Get current page's base height (at zoom 1.0)
    // pagesInfo height is already scaled, divide by scale to get base dimensions
    const currentPageInfo = pagesInfo.find(p => p.pageNumber === currentPage) || pagesInfo[0];
    const pageHeightAtCurrentScale = currentPageInfo.height;
    const baseHeight = pageHeightAtCurrentScale / currentPageInfo.scale;

    // Calculate zoom to fit height
    const newZoom = (containerHeight - padding) / baseHeight;
    setZoom(Math.max(minZoom, Math.min(newZoom, maxZoom)));

    // Scroll to current page to show it in view
    requestAnimationFrame(() => {
      const pageElement = document.getElementById(`page-wrapper-${currentPage}`);
      if (pageElement && containerRef.current) {
        const containerTop = containerRef.current.scrollTop;
        const containerRect = containerRef.current.getBoundingClientRect();
        const pageRect = pageElement.getBoundingClientRect();
        const offset = pageRect.top - containerRect.top + containerTop;
        containerRef.current.scrollTop = offset;
      }
    });
  }, [containerRef, pagesInfo, currentPage, minZoom, maxZoom, setZoom]);

  /**
   * Navigate to specific page
   */
  const goToPage = useCallback((page: number) => {
    const pageElement = document.getElementById(`page-wrapper-${page}`);
    if (pageElement && containerRef.current) {
      // Calculate scroll position relative to container
      const containerTop = containerRef.current.scrollTop;
      const containerRect = containerRef.current.getBoundingClientRect();
      const pageRect = pageElement.getBoundingClientRect();

      // Calculate offset from container top to page top
      const offset = pageRect.top - containerRect.top + containerTop;

      // Scroll container to show page at top
      containerRef.current.scrollTop = offset;
      setCurrentPage(page);
    }
  }, [containerRef]);

  /**
   * Navigate to next page
   */
  const nextPage = useCallback(() => {
    if (currentPage < pagesInfo.length) {
      goToPage(currentPage + 1);
    }
  }, [currentPage, pagesInfo.length, goToPage]);

  /**
   * Navigate to previous page
   */
  const previousPage = useCallback(() => {
    if (currentPage > 1) {
      goToPage(currentPage - 1);
    }
  }, [currentPage, goToPage]);

  /**
   * Track current page using Intersection Observer
   * Updates currentPage as user scrolls through document
   */
  useEffect(() => {
    if (pagesInfo.length === 0 || !containerRef.current) return;

    const options = {
      root: containerRef.current,
      rootMargin: '-50% 0px -50% 0px', // Trigger when page is at center
      threshold: 0,
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const pageNumber = parseInt(
            entry.target.id.replace('page-wrapper-', ''),
            10
          );
          if (!isNaN(pageNumber)) {
            setCurrentPage(pageNumber);
          }
        }
      });
    }, options);

    pagesInfo.forEach((pageInfo) => {
      const element = document.getElementById(`page-wrapper-${pageInfo.pageNumber}`);
      if (element) {
        observer.observe(element);
      }
    });

    return () => observer.disconnect();
  }, [pagesInfo, containerRef]);

  return {
    currentPage,
    setCurrentPage,
    zoomIn,
    zoomOut,
    resetZoom,
    fitWidth,
    fitHeight,
    goToPage,
    nextPage,
    previousPage,
  };
};
